import { Button } from "react-bootstrap";

import { useRef, useState, useEffect, useCallback } from "react";
import * as APIModels from "../../../../models";
import Select, { ActionMeta } from "react-select";
import { SubmissionService } from "../../../../services/SubmissionService";
import CaseSearch from "../CaseSearch";
import Pleadings from "../Pleadings";
import Fees from "../Fees";
import { EmptyGuid } from "../../../../models";
import { useParams } from "react-router-dom";
import TylerPleadings, {
    MyOptionType as SecurityOptions,
} from "../TylerPleadings";
import { Checkbox } from "@material-ui/core";
import CaseSearchLong from "../CaseSearchLong";
import NotesModal from "../modals/NotesModal";
import AddPartyAttorneyModal from "../modals/AddPartyAttorneyModal";
import AddFilingPartyModal from "../modals/AddFilingPartyModal";
import FilingPartiesSelect, { filingPartyKey } from "../FilingPartiesSelect";
import canSubmit from "./TylerDocumentUtils";

type MyOptionType = { label: string; value: string };

interface QueryParams {
    submissionId?: string;
    attorneyId?: string;
    filter?: string;
    state?: string;
}

export default function ExistingCase({
    submission,
    onSelectDocument,
    setNextSubmission,
    filter,
    onCaseDataChange,
}: {
    submission: APIModels.Submission;
    onSelectDocument: (doc: APIModels.Document, index: number) => void;
    setNextSubmission: (next: APIModels.Submission) => void;
    filter?: string;
    onCaseDataChange?: (caseData: APIModels.CaseData) => void;
}) {
    const [completedSubmission, setCompletedSubmission] = useState(submission);
    const [completedDocs, setCompletedDocs] = useState(submission.documents!);
    const [completedFees, setCompletedFees] = useState(submission.fees ?? []);
    const [submitting, setSubmitting] = useState(false);
    const [currentCounty, setCurrentCounty] = useState(
        submission.caseData?.county
    );
    const [caseAlert, setCaseAlert] = useState("");
    const [caseAlertCycles, setCaseAlertCycles] = useState(0);
    const [override, setOverride] = useState(false);
    const [feePay, setFeepay] = useState(submission.feePay ?? false);
    const [requiresFeeWaiver, setRequiresFeeWaiver] = useState(
        submission.requiresFeeWaiver ?? false
    );
    const [isProSe, setIsProSe] = useState(submission.isProSe ?? false);
    const [associateWithFilingParties, setAssociateWithFilingParties] =
        useState(
            submission.caseData?.leadAttorney?.associateWithFilingParties ??
                false
        );
    const [filingPartyIDs, setFilingPartyIDs] = useState<string[]>(
        submission.filingPartyIDs ?? []
    );
    const [primaryPartyId, setPrimaryPartyId] = useState<string | undefined>(
        submission.filingPartyID
    );
    const [showAddPartyModal, setShowAddPartyModal] = useState(false);
    const [showAddFilingPartyModal, setShowAddFilingPartyModal] =
        useState(false);
    const [feeLoading, setFeeLoading] = useState<boolean>(false);
    const [notesCheck, setNotesCheck] = useState(false);
    const [isSubmissionLocked, setIsSubmissionLocked] = useState(
        (submission.portalIdentifier == "TX" ||
            submission.portalIdentifier == "IL" ||
            submission.portalIdentifier == "IN" ||
            submission.portalIdentifier == "NC" ||
            submission.portalIdentifier == "CA" ||
            (submission.portalIdentifier == "GA" &&
                completedSubmission.caseData &&
                completedSubmission.caseData.caseTypeID !=
                    APIModels.EmptyGuid) ||
            submission.portalIdentifier == "MD") &&
            submission.documents
                ?.map(
                    (d) =>
                        d.securityID !== null ||
                        d.securityID !== undefined ||
                        d.securityID !== "" ||
                        d.securityID !== APIModels.EmptyGuid
                )
                .reduce((a, b) => a && b)
    );

    const [showParties, setShowParties] = useState(false);
    const partiesDiv = useRef<HTMLDivElement>(null);
    const [showNotesModal, setShowNotesModal] = useState(
        (submission.notes?.map((o, i) => o.note ?? "")?.join(" ") ?? "")
            .length > 0
    );
    const [lockedError, setLockedError] = useState(false);
    const [isSecurityRequired, setIsSecurityRequired] = useState(false);
    const [availableSecurityTypes, setAvailableSecurityTypes] = useState<
        MyOptionType[]
    >([]);
    const isFilingComponentRequired = true;

    useEffect(() => {
        setCompletedSubmission(submission);
    }, [submission]);

    const handleFeeLoading = () => {
        console.log({ "Handeling fees loading: ": !feeLoading });
        setFeeLoading((prev) => {
            return !prev;
        });
    };

    const onSearchComplete = (
        request: APIModels.CaseSearch,
        result: APIModels.CourtCase
    ) => {
        const updatedCaseData =
            completedSubmission.caseData ?? new APIModels.CaseData();
        updatedCaseData.ucn = result.ucn;
        updatedCaseData.county = result.county;
        updatedCaseData.countyID = request.countyID;
        updatedCaseData.division = result.division;
        updatedCaseData.divisionID =
            result.divisionID && result.divisionID != EmptyGuid
                ? result.divisionID
                : request.divisionID;
        updatedCaseData.courtCaseNumber =
            result.caseNumber ?? request.caseNumber;
        updatedCaseData.caseTitle = result.caseTitle;
        updatedCaseData.caseTypeID = result.caseTypeID;
        updatedCaseData.caseSubTypeID = result.caseSubTypeID;
        updatedCaseData.year = request.year;
        updatedCaseData.sequenceNumber = request.sequenceNumber;

        setCompletedSubmission({
            ...submission,
            caseData: updatedCaseData,
            parties: result.parties,
        });

        if (onCaseDataChange) {
            onCaseDataChange(updatedCaseData);
        }

        setCurrentCounty(updatedCaseData.county);

        setCaseAlert("red-fade-in");
    };

    const onOptInComplete = (result?: APIModels.Submission) => {
        // Move on to the next submission, mirroring onSubmit's navigation.
        if (result && result.id) {
            window.location.href = `/efile?submissionId=${result.id}&filter=${filter}`;
        } else {
            window.location.href = "/queue/" + filter;
        }
    };

    const onUpdatePartiesInCase = (parties: APIModels.Party[]) => {
        // Added parties/attorneys live on the submission in memory; they persist
        // when the user clicks Submit.
        setCompletedSubmission({ ...completedSubmission, parties });
    };

    const onAddFilingParty = (party: APIModels.Party) => {
        // A new party has no row id or court participant id yet, so give it a row
        // id here. That makes it selectable as a filing/primary party, and it's the
        // id efme-common matches new (not-yet-on-the-court-case) parties on.
        const added = {
            ...party,
            id: window.crypto.randomUUID(),
            submissionID: completedSubmission.id,
        };
        onUpdatePartiesInCase([...(completedSubmission.parties ?? []), added]);
        setFilingPartyIDs((ids) => [...ids, added.id]);
        setShowAddFilingPartyModal(false);
    };

    const onCountyChangeInSearch = (countyName: string) => {
        setCurrentCounty(countyName);

        // Create updated case data with new county
        const updatedCaseData =
            completedSubmission.caseData ?? new APIModels.CaseData();
        updatedCaseData.county = countyName;

        if (onCaseDataChange) {
            onCaseDataChange(updatedCaseData);
        }
    };

    const onUpdateDoc = (doc: APIModels.Document) => {
        const docs = [...completedDocs!];
        const index = completedDocs?.findIndex((v) => v.id == doc.id);
        docs[index!].documentType = doc.documentType;
        docs[index!].documentGroup = doc.documentGroup;
        docs[index!].documentTypeID = doc.documentTypeID;
        docs[index!].fees = doc.fees;
        docs[index!].description = doc.description;
        docs[index!].comments = doc.comments;
        docs[index!].filingCodeID = doc.filingCodeID;
        docs[index!].filingComponentID = doc.filingComponentID;
        docs[index!].securityID = doc.securityID ? doc.securityID : EmptyGuid;
        docs[index!].optionalServices = doc.optionalServices;
        // console.log(docs);
        setCompletedDocs(docs);
    };

    const onFeeChange = (fees: APIModels.FeeType[]) => {
        setCompletedFees(fees);
    };

    const isTyler = (): boolean =>
        submission.portalIdentifier == "TX" ||
        submission.portalIdentifier == "IL" ||
        submission.portalIdentifier == "IN" ||
        submission.portalIdentifier == "NC" ||
        submission.portalIdentifier == "CA" ||
        (submission.portalIdentifier == "GA" &&
            completedSubmission.caseData &&
            completedSubmission.caseData.caseTypeID != APIModels.EmptyGuid) ||
        submission.portalIdentifier == "MD";

    const doAllDocumentsHaveSecurity = (): boolean => {
        let result;
        if (completedDocs.length === 1) {
            result =
                completedDocs[0].securityID !== null &&
                completedDocs[0].securityID !== undefined &&
                completedDocs[0].securityID !== "" &&
                completedDocs[0].securityID !== APIModels.EmptyGuid;
        } else {
            result =
                completedDocs
                    .map((doc) => {
                        return doc.securityID;
                    })
                    .map(
                        (doc) =>
                            doc !== null &&
                            doc !== undefined &&
                            doc !== "" &&
                            doc !== APIModels.EmptyGuid
                    )
                    .reduce((a, b) => a && b) ?? false;
        }
        return result;
    };
    const doAllHaveFilingCodeComponents = (): boolean => {
        let result;
        if (completedDocs.length === 1) {
            result =
                completedDocs[0].filingComponentID !== null &&
                completedDocs[0].filingComponentID !== undefined &&
                completedDocs[0].filingComponentID !== "" &&
                completedDocs[0].filingComponentID !== APIModels.EmptyGuid;
        } else {
            result =
                completedDocs
                    .map((doc) => {
                        return doc.filingComponentID;
                    })
                    .map(
                        (doc) =>
                            doc !== null &&
                            doc !== undefined &&
                            doc !== "" &&
                            doc !== APIModels.EmptyGuid
                    )
                    .reduce((a, b) => a && b) ?? false;
        }
        return result;
    };
    const doAllHaveFilingCode = (): boolean => {
        let result;
        if (completedDocs.length === 1) {
            result =
                completedDocs[0].filingCodeID !== null &&
                completedDocs[0].filingCodeID !== undefined &&
                completedDocs[0].filingCodeID !== "" &&
                completedDocs[0].filingCodeID !== APIModels.EmptyGuid;
        } else {
            result =
                completedDocs
                    .map((doc) => {
                        return doc.filingCodeID;
                    })
                    .map(
                        (doc) =>
                            doc !== null &&
                            doc !== undefined &&
                            doc !== "" &&
                            doc !== APIModels.EmptyGuid
                    )
                    .reduce((a, b) => a && b) ?? false;
        }
        return result;
    };

    const securityTypesRequired = (opts: MyOptionType[]) => {
        setIsSecurityRequired(opts.length !== 0);
        setAvailableSecurityTypes(opts);
        return opts.length !== 0;
    };

    useEffect(() => {
        console.log({
            "Submission will need to have security types because it is a Tyler State":
                isTyler(),
        });
        console.log({
            // if it do not
            "Submission will be locked because not all documents have security types":
                !doAllDocumentsHaveSecurity(),
        });
    }, [completedDocs]);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        console.log(completedSubmission);
        const submittable = canSubmit(
            completedSubmission,
            availableSecurityTypes
        );
        console.log(submittable);
        if (!submittable) {
            e.preventDefault();
            setLockedError(true);
            return;
        }
        setSubmitting(true);
        // Drop selections for parties no longer on the case (e.g. after a
        // different case was searched).
        const isOnCase = (id: string) =>
            (completedSubmission.parties ?? []).some(
                (p) => filingPartyKey(p) === id
            );
        // "Associate with filing parties" travels on the lead attorney, where
        // efme-common reads it (CaseData.LeadAttorney.AssociateWithFilingParties).
        // With no lead attorney, one carrying only the flag is sent when checked.
        const leadAttorney = completedSubmission.caseData?.leadAttorney;
        const caseData =
            completedSubmission.caseData &&
            (leadAttorney || associateWithFilingParties)
                ? {
                      ...completedSubmission.caseData,
                      leadAttorney: {
                          ...(leadAttorney ?? new APIModels.Party()),
                          associateWithFilingParties:
                              associateWithFilingParties,
                      },
                  }
                : completedSubmission.caseData;
        SubmissionService.submit({
            attorneyId: completedSubmission.attorneyID,
            filter: filter,
            state: completedSubmission.portalIdentifier,
            body: {
                ...completedSubmission,
                caseData: caseData,
                documents: completedDocs,
                fees: completedFees,
                override: override,
                feePay: feePay,
                requiresFeeWaiver: requiresFeeWaiver,
                isProSe: isProSe,
                // Filing Parties and Primary Party are independent fields; each
                // is sent as selected.
                filingPartyIDs: filingPartyIDs.filter(isOnCase),
                filingPartyID:
                    primaryPartyId && isOnCase(primaryPartyId)
                        ? primaryPartyId
                        : undefined,
            },
        }).then(
            (resp) => {
                console.log(resp);
                if (resp.data && resp.data.id) {
                    window.location.href = `/efile?submissionId=${resp.data.id}&filter=${filter}`;
                } else {
                    window.location.href = "/queue/" + filter;
                }
            },
            (_error) => {
                setSubmitting(false);
                window.location.href = "/queue/" + filter;
            }
        );
        e.preventDefault();
    };

    const lockSubmission = (docHasSecurity: Map<string, boolean>): void => {
        console.log({ "lockSubmission Debug": docHasSecurity });
        const isLocked = Array.from(docHasSecurity.values())
            .map((value, k) => {
                console.log({ "lockSubmission inMap Debug": [value, k] });
                return value;
            })
            .reduce((a, b) => a && b);
        console.log({ "lockSubmission Debug isLocked": isLocked });
        // if true allow submission to be submitted
        setIsSubmissionLocked(!isLocked);
    };

    useEffect(() => {
        if (caseAlert === "red-fade-out") {
            setTimeout(() => setCaseAlert("red-fade-in"), 500);
        } else if (caseAlert === "red-fade-in") {
            setTimeout(() => setCaseAlert(""), 1000);
        } else if (caseAlert === "" && caseAlertCycles < 3) {
            setTimeout(() => setCaseAlert("red-fade-out"), 1000);
            setCaseAlertCycles(caseAlertCycles + 1);
        } else {
            setCaseAlertCycles(0);
        }
    }, [caseAlert]);

    window.addEventListener("keydown", (event: KeyboardEvent) => {
        switch (event.code) {
            case "F1":
                event.stopPropagation();
                event.stopImmediatePropagation();
                event.cancelBubble = true;
                event.preventDefault();
                event.returnValue = false;
                (
                    document.getElementById("flush-headingOne")
                        ?.firstElementChild as HTMLButtonElement
                ).click();

                break;
            case "F2":
                event.stopPropagation();
                event.stopImmediatePropagation();
                event.cancelBubble = true;
                event.preventDefault();
                event.returnValue = false;
                (
                    document.getElementById("flush-headingTwo")
                        ?.firstElementChild as HTMLButtonElement
                ).click();
                break;
            case "F3":
                event.stopPropagation();
                event.stopImmediatePropagation();
                event.cancelBubble = true;
                event.preventDefault();
                event.returnValue = false;
                (
                    document.getElementById("flush-headingThree")
                        ?.firstElementChild as HTMLButtonElement
                ).click();
                break;
            default:
                break;
        }
    });

    const hoverShowParties = useCallback(() => {
        const rect = partiesDiv.current?.getBoundingClientRect() ?? null;
        if (rect === null) {
            setShowParties(false);
            return;
        }
        return (
            <div
                style={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    width: "400px",
                    backgroundColor: "white",
                    pointerEvents: "none",
                    padding: "10px",
                    border: "1px solid #DDD",
                    borderRadius: "8px",
                    zIndex: 100,
                }}
            >
                <table>
                    {completedSubmission.parties
                        ?.filter((v) => v.partyType?.indexOf("Attorney") == -1)
                        .map((v) => (
                            <tr>
                                <td>{v.partyType}</td>
                                <td>
                                    {v.name
                                        ? v.name
                                        : v.firstName + " " + v.lastName}
                                </td>
                            </tr>
                        ))}
                </table>
            </div>
        );
    }, [completedSubmission]);

    const handleMouseOver = (event: React.MouseEvent<HTMLElement>) => {
        setShowParties(true);
    };

    const handleMouseOut = (event: React.MouseEvent<HTMLImageElement>) => {
        setShowParties(false);
    };
    const onCancelNotes = () => {
        setShowNotesModal(false);
        setNotesCheck(false);
    };
    const onConfirmNotes = () => {
        setShowNotesModal(false);
        setNotesCheck(false);
    };

    return (
        <>
            <form className="needs-validation" onSubmit={onSubmit}>
                <NotesModal
                    show={showNotesModal}
                    onCancelNotes={onCancelNotes}
                    onConfirmNotes={onConfirmNotes}
                    notes={
                        submission.notes
                            ?.map((o, i) => o.note ?? "")
                            ?.join(" ") ?? ""
                    }
                ></NotesModal>
                <div>
                    <div
                        className="mb-3"
                        ref={partiesDiv}
                        onMouseEnter={handleMouseOver}
                        onMouseLeave={handleMouseOut}
                    >
                        {showParties && hoverShowParties()}
                        <label className="form-label">UCN</label>
                        <a style={{ display: "block" }}>
                            {completedSubmission.caseData?.ucn}
                        </a>
                    </div>
                    <div className={`mb-3 ${caseAlert}`}>
                        {(completedSubmission.caseData?.caseTitle === "" ||
                            completedSubmission.caseData?.caseTitle === null) &&
                        completedSubmission.caseData?.ucn != "" &&
                        completedSubmission.caseData?.ucn != null ? (
                            <>
                                <label className="form-label">Case Found</label>
                                <div>
                                    <b>
                                        Case Number :{" "}
                                        {
                                            completedSubmission.caseData
                                                ?.courtCaseNumber
                                        }
                                    </b>
                                    <b></b>
                                </div>
                            </>
                        ) : (
                            <>
                                <label className="form-label">Case Title</label>
                                <div>
                                    <b>
                                        {
                                            completedSubmission.caseData
                                                ?.caseTitle
                                        }
                                    </b>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div
                    className="accordion accordion-flush"
                    id="accordionFlushExample"
                >
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="flush-headingOne">
                            <button
                                className="accordion-button"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#flush-collapseOne"
                                aria-expanded="true"
                                aria-controls="flush-collapseOne"
                            >
                                Case Search
                            </button>
                        </h2>
                        <div
                            id="flush-collapseOne"
                            className="accordion-collapse collapse show"
                            aria-labelledby="flush-headingOne"
                            data-bs-parent="#accordionFlushExample"
                        >
                            <div className="accordion-body">
                                <CaseSearchLong
                                    submission={completedSubmission}
                                    onSearchComplete={onSearchComplete}
                                    onCountyChange={onCountyChangeInSearch}
                                    onOptInComplete={onOptInComplete}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="flush-headingTwo">
                            <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#flush-collapseTwo"
                                aria-expanded="false"
                                aria-controls="flush-collapseTwo"
                            >
                                Documents
                            </button>
                        </h2>
                        <div
                            id="flush-collapseTwo"
                            className="accordion-collapse collapse"
                            aria-labelledby="flush-headingTwo"
                            data-bs-parent="#accordionFlushExample"
                        >
                            <div className="accordion-body">
                                {submission.portalIdentifier == "TX" ||
                                submission.portalIdentifier == "IL" ||
                                submission.portalIdentifier == "IN" ||
                                submission.portalIdentifier == "NC" ||
                                submission.portalIdentifier == "CA" ||
                                (submission.portalIdentifier == "GA" &&
                                    completedSubmission.caseData &&
                                    completedSubmission.caseData.caseTypeID !=
                                        APIModels.EmptyGuid) ||
                                submission.portalIdentifier == "MD" ? (
                                    <TylerPleadings
                                        submission={completedSubmission}
                                        originalDocs={completedDocs}
                                        onSelectDocument={onSelectDocument}
                                        onUpdateDoc={onUpdateDoc}
                                        submissionLocked={lockSubmission}
                                        securityTypes={securityTypesRequired}
                                    />
                                ) : (
                                    <Pleadings
                                        submission={completedSubmission}
                                        originalDocs={completedDocs}
                                        onSelectDocument={onSelectDocument}
                                        onUpdateDoc={onUpdateDoc}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="accordion-item">
                        <h2
                            className="accordion-header"
                            id="flush-headingThree"
                        >
                            <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#flush-collapseThree"
                                aria-expanded="false"
                                aria-controls="flush-collapseThree"
                            >
                                Fees
                            </button>
                        </h2>
                        <div
                            id="flush-collapseThree"
                            className="accordion-collapse collapse"
                            aria-labelledby="flush-headingThree"
                            data-bs-parent="#accordionFlushExample"
                        >
                            <div className="accordion-body">
                                <Fees
                                    submission={completedSubmission}
                                    fees={completedFees}
                                    handleFeeLoading={handleFeeLoading}
                                    onFeeChange={onFeeChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12" style={{ textAlign: "center" }}>
                        {submitting ? (
                            <button
                                className="btn btn-primary"
                                type="button"
                                disabled={true}
                            >
                                <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Submitting...
                            </button>
                        ) : (
                            <button
                                className="btn btn-primary"
                                type="submit"
                                disabled={
                                    submission.notes != null &&
                                    submission.notes != undefined &&
                                    submission.notes.length > 0 &&
                                    submission.notes[0].note != "" &&
                                    notesCheck == false
                                }
                            >
                                Submit
                            </button>
                        )}
                    </div>
                </div>
                {submission.notes != null &&
                submission.notes != undefined &&
                submission.notes.length > 0 &&
                submission.notes[0].note != "" ? (
                    <div className="row">
                        <div
                            className="col-12 mt-2"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                className="form-check"
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <input
                                    className="form-check-input me-2"
                                    id="ckNotes"
                                    type="checkbox"
                                    checked={notesCheck}
                                    onChange={(event) =>
                                        setNotesCheck(
                                            event.currentTarget.checked
                                        )
                                    }
                                />
                                <label
                                    htmlFor="ckNotes"
                                    className="form-check-label"
                                >
                                    Notes Acknowledgment
                                </label>
                            </div>
                        </div>

                        <div
                            className="col-12 mt-3"
                            style={{ textAlign: "center" }}
                        >
                            <button
                                className="btn btn-primary"
                                type="button"
                                onClick={() => {
                                    setShowNotesModal(true);
                                    setNotesCheck(false);
                                }}
                            >
                                Check Notes
                            </button>
                        </div>
                    </div>
                ) : (
                    <></>
                )}
                <div className="row mt-2">
                    <div
                        className="col-12"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            className="col-2"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    id="ckOverride"
                                    type="checkbox"
                                    checked={override ?? false}
                                    onChange={(event) =>
                                        setOverride(event.currentTarget.checked)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="ckOverride"
                                >
                                    Override
                                </label>
                            </div>
                        </div>
                        <div
                            className="col-2"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    id="ckFeePay"
                                    type="checkbox"
                                    checked={feePay ?? false}
                                    onChange={(event) =>
                                        setFeepay(event.currentTarget.checked)
                                    }
                                />
                                <label
                                    htmlFor="ckFeePay"
                                    className="form-check-label"
                                >
                                    Fee Pay
                                </label>
                            </div>
                        </div>
                        <div
                            className="col-auto"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    id="ckRequiresFeeWaiver"
                                    type="checkbox"
                                    checked={requiresFeeWaiver}
                                    onChange={(event) =>
                                        setRequiresFeeWaiver(
                                            event.currentTarget.checked
                                        )
                                    }
                                />
                                <label
                                    htmlFor="ckRequiresFeeWaiver"
                                    className="form-check-label"
                                >
                                    Requires Fee Waiver
                                </label>
                            </div>
                        </div>
                        <div
                            className="col-auto"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    id="ckIsProSe"
                                    type="checkbox"
                                    checked={isProSe}
                                    onChange={(event) =>
                                        setIsProSe(event.currentTarget.checked)
                                    }
                                />
                                <label
                                    htmlFor="ckIsProSe"
                                    className="form-check-label"
                                >
                                    Pro Se
                                </label>
                            </div>
                        </div>
                        <div
                            className="col-auto"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    id="ckAssociateWithFilingParties"
                                    type="checkbox"
                                    checked={associateWithFilingParties}
                                    onChange={(event) =>
                                        setAssociateWithFilingParties(
                                            event.currentTarget.checked
                                        )
                                    }
                                />
                                <label
                                    htmlFor="ckAssociateWithFilingParties"
                                    className="form-check-label"
                                >
                                    Associate with Filing Parties
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <FilingPartiesSelect
                    parties={completedSubmission.parties ?? []}
                    selectedIds={filingPartyIDs}
                    onChange={setFilingPartyIDs}
                    primaryId={primaryPartyId}
                    onPrimaryChange={setPrimaryPartyId}
                    filingPartyResponsible={
                        completedSubmission.caseData?.filingPartyResponsible
                    }
                    onAddParty={() => setShowAddFilingPartyModal(true)}
                />
                <AddFilingPartyModal
                    show={showAddFilingPartyModal}
                    onClose={() => setShowAddFilingPartyModal(false)}
                    onSave={onAddFilingParty}
                    caseData={completedSubmission.caseData}
                />
                {lockedError ? (
                    <div className="row mt-2">
                        <div
                            className="col-12"
                            style={{ textAlign: "center", color: "red" }}
                        >
                            This submission cannot be submitted, until you
                            ensure that every document in the case has: a Filing
                            Code, a FilingComponent Code, and a Security Type.
                        </div>
                    </div>
                ) : (
                    <></>
                )}
                {/* The "Add a Party/Attorney" button is removed for now; the modal
                    stays wired up so the button can be restored later. */}
                <AddPartyAttorneyModal
                    show={showAddPartyModal}
                    onClose={() => setShowAddPartyModal(false)}
                    submission={completedSubmission}
                    parties={completedSubmission.parties ?? []}
                    onUpdateParties={onUpdatePartiesInCase}
                />
            </form>
        </>
    );
}
